import React, { useState } from 'react';

import PersonAvatar from 'app/components/CommonStyled/PersonAvatar';
import SideDrawer from 'app/components/CommonStyled/SideDrawer';
import GlobalPersonDrawer from 'app/components/Drawers/GlobalPerson/GlobalPersonDrawer';
import useBehavioral from 'hooks/useBehavioral';
import useIsMobile from 'hooks/useIsMobile';
import { limitText } from 'utils';

import {
  SomeoneNewAuthor,
  SomeoneNewBackgroundCardOne,
  SomeoneNewBackgroundCardPersonAvatar,
  SomeoneNewBackgroundCardQuoteSkeleton,
  SomeoneNewBackgroundCardQuoteSkeletonContainer,
  SomeoneNewBackgroundCardTwo,
  SomeoneNewBio,
  SomeoneNewContainer,
  SomeoneNewEndQuoteContainer,
  SomeoneNewImageContainer,
  SomeoneNewLearnMore,
  SomeoneNewNext,
  SomeoneNewPrevious,
  SomeoneNewStartQuoteContainer,
  SomeoneNewTextContainer,
  SomeoneNewWildcardContainer,
  SomeoneNewWrapper,
} from './styled';

function getPersonDescription(person) {
  if (!person) return '';

  let personDescription = '';
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
  };

  if (person.engagement?.start_date) {
    const formattedDate = formatDate(person.engagement.start_date);
    personDescription = `I started with ${person.company_name} on ${formattedDate}`;
    if (person.engagement?.role) {
      personDescription += `. My current role is ${person.engagement.role}.`;
    }
  } else {
    personDescription = `I work at ${person.company_name}`;
    if (person.engagement?.role) {
      personDescription += `. My current role is ${person.engagement.role}.`;
    }
  }

  return personDescription;
}

interface MeetSomeoneNewProps {
  people: any;
  fromPassion?: boolean;
  hideWildcard?: boolean;
}

export function MeetSomeoneNew({
  people,
  fromPassion = false,
  hideWildcard = false,
}: MeetSomeoneNewProps) {
  const meetSomeoneNewCarouselClickedBehavioral = useBehavioral(
    'MEET_SOMEONE_NEW_CAROUSEL_CLICKED'
  );
  const meetSomeoneNewViewProfileClickedBehavioral = useBehavioral(
    'MEET_SOMEONE_NEW_CAROUSEL_VIEW_PROFILE_CLICKED'
  );
  const [index, setIndex] = useState<number>(0);
  const [showPersonDrawer, setShowPersonDrawer] = useState<boolean>(false);
  const [triggerNext, setTriggerNext] = useState<boolean>(false);
  const [triggerPrevious, setTriggerPrevious] = useState<boolean>(false);

  const isMobile = useIsMobile();

  const previousPerson = () => {
    meetSomeoneNewCarouselClickedBehavioral();
    setTriggerPrevious(true);
    setTimeout(() => {
      index === 0 ? setIndex(people.length - 1) : setIndex(index - 1);
      setTriggerPrevious(false);
    }, 1000);
  };

  const nextPerson = () => {
    meetSomeoneNewCarouselClickedBehavioral();
    setTriggerNext(true);
    setTimeout(() => {
      index === people.length - 1 ? setIndex(0) : setIndex(index + 1);
      setTriggerNext(false);
    }, 1000);
  };

  const handlePersonClick = () => {
    meetSomeoneNewViewProfileClickedBehavioral({
      related_object_uuid: people[index]?.slug,
      related_object_type: 'Person',
    });
    setShowPersonDrawer(true);
  };

  return (
    <SomeoneNewWrapper fromPassion={fromPassion}>
      <SomeoneNewBackgroundCardOne triggerNext={triggerNext} triggerPrevious={triggerPrevious}>
        <SomeoneNewBackgroundCardPersonAvatar />
        <SomeoneNewBackgroundCardQuoteSkeletonContainer>
          <SomeoneNewBackgroundCardQuoteSkeleton width={70} />
          <SomeoneNewBackgroundCardQuoteSkeleton width={80} />
          <SomeoneNewBackgroundCardQuoteSkeleton width={50} />
        </SomeoneNewBackgroundCardQuoteSkeletonContainer>
      </SomeoneNewBackgroundCardOne>
      <SomeoneNewBackgroundCardTwo triggerChange={triggerNext || triggerPrevious}>
        <SomeoneNewBackgroundCardPersonAvatar />
        <SomeoneNewBackgroundCardQuoteSkeletonContainer>
          <SomeoneNewBackgroundCardQuoteSkeleton width={70} />
          <SomeoneNewBackgroundCardQuoteSkeleton width={80} />
          <SomeoneNewBackgroundCardQuoteSkeleton width={50} />
        </SomeoneNewBackgroundCardQuoteSkeletonContainer>
      </SomeoneNewBackgroundCardTwo>
      {people.length > 1 && (
        <SomeoneNewPrevious
          onClick={() => {
            if (!triggerPrevious && !triggerNext) {
              previousPerson();
            }
          }}
        >
          {'<'}
        </SomeoneNewPrevious>
      )}
      {people.length > 1 && (
        <SomeoneNewNext
          onClick={() => {
            if (!triggerPrevious && !triggerNext) {
              nextPerson();
            }
          }}
        >
          {'>'}
        </SomeoneNewNext>
      )}
      {!hideWildcard ? (
        <SomeoneNewWildcardContainer>
          <WildcardIcon />
        </SomeoneNewWildcardContainer>
      ) : null}
      <SomeoneNewContainer
        triggerNext={triggerNext}
        triggerPrevious={triggerPrevious}
        onClick={handlePersonClick}
      >
        <SomeoneNewImageContainer>
          <PersonAvatar
            size={64}
            avatar={people[index]?.avatar}
            highlighted
            name={people[index]?.full_name.split(' ')[0]}
            lastName={people[index]?.full_name.split(' ')[1]}
            openDrawer
          />
        </SomeoneNewImageContainer>
        <SomeoneNewTextContainer>
          <SomeoneNewStartQuoteContainer>
            <StartQuoteIcon />
          </SomeoneNewStartQuoteContainer>
          <SomeoneNewEndQuoteContainer>
            <EndQuoteIcon />
          </SomeoneNewEndQuoteContainer>
          <SomeoneNewBio>
            {limitText(
              people[index]?.quote?.text || people[index]?.quote || getPersonDescription(people[index]),
              isMobile ? 35 : 110
            )}
          </SomeoneNewBio>
          <SomeoneNewAuthor>{people[index]?.full_name}</SomeoneNewAuthor>
        </SomeoneNewTextContainer>
        <SomeoneNewLearnMore>View Profile</SomeoneNewLearnMore>
      </SomeoneNewContainer>
      {showPersonDrawer && (
        <SideDrawer closeDrawer={() => setShowPersonDrawer(false)} withClose>
          <GlobalPersonDrawer slug={people[index]?.slug} />
        </SideDrawer>
      )}
    </SomeoneNewWrapper>
  );
}

function WildcardIcon() {
  return (
    <svg
      width="121"
      height="122"
      viewBox="0 0 121 122"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M59.0031 1.68939C59.7989 0.791215 61.2011 0.791216 61.9969 1.6894L67.5481 7.95443C68.1821 8.66989 69.2337 8.83672 70.058 8.35259L77.2523 4.12692C78.286 3.51979 79.6179 3.95214 80.0979 5.05063L83.4643 12.7546C83.8462 13.6286 84.792 14.1113 85.7237 13.9078L93.8683 12.1289C95.0363 11.8738 96.1657 12.6936 96.2851 13.8831L97.1278 22.2809C97.2226 23.2263 97.9696 23.9744 98.9148 24.0709L107.224 24.9185C108.409 25.0394 109.227 26.1634 108.976 27.3283L107.2 35.5953C107.001 36.5208 107.479 37.4589 108.344 37.8432L116.007 41.2472C117.092 41.7295 117.521 43.0467 116.926 44.0755L112.7 51.391C112.227 52.2087 112.391 53.2455 113.093 53.8775L119.35 59.514C120.232 60.3084 120.232 61.6916 119.35 62.486L113.093 68.1225C112.391 68.7545 112.227 69.7913 112.7 70.609L116.926 77.9245C117.521 78.9533 117.092 80.2705 116.007 80.7528L108.344 84.1568C107.479 84.5411 107.001 85.4792 107.2 86.4047L108.976 94.6717C109.227 95.8366 108.409 96.9606 107.224 97.0815L98.9148 97.9291C97.9696 98.0256 97.2226 98.7737 97.1278 99.7191L96.2851 108.117C96.1657 109.306 95.0363 110.126 93.8683 109.871L85.7237 108.092C84.792 107.889 83.8462 108.371 83.4643 109.245L80.0979 116.949C79.6179 118.048 78.286 118.48 77.2523 117.873L70.058 113.647C69.2337 113.163 68.1821 113.33 67.5481 114.046L61.9969 120.311C61.2011 121.209 59.7989 121.209 59.0031 120.311L53.4519 114.046C52.8179 113.33 51.7663 113.163 50.942 113.647L43.7477 117.873C42.714 118.48 41.3821 118.048 40.9021 116.949L37.5357 109.245C37.1538 108.371 36.208 107.889 35.2763 108.092L27.1317 109.871C25.9637 110.126 24.8343 109.306 24.7149 108.117L23.8722 99.7191C23.7774 98.7737 23.0304 98.0256 22.0852 97.9291L13.7759 97.0815C12.5906 96.9606 11.7732 95.8366 12.0235 94.6717L13.7997 86.4047C13.9985 85.4792 13.5213 84.5411 12.6562 84.1568L4.9934 80.7528C3.90752 80.2705 3.47917 78.9533 4.0736 77.9245L8.30026 70.609C8.77271 69.7913 8.60878 68.7545 7.90709 68.1225L1.64972 62.486C0.767802 61.6916 0.7678 60.3084 1.64972 59.514L7.90708 53.8775C8.60878 53.2455 8.77272 52.2087 8.30026 51.391L4.0736 44.0755C3.47917 43.0467 3.90752 41.7295 4.9934 41.2472L12.6562 37.8432C13.5213 37.4589 13.9985 36.5208 13.7997 35.5953L12.0235 27.3283C11.7732 26.1634 12.5906 25.0394 13.7759 24.9185L22.0852 24.0709C23.0304 23.9744 23.7774 23.2263 23.8722 22.2809L24.7149 13.8831C24.8343 12.6936 25.9637 11.8738 27.1317 12.1289L35.2763 13.9078C36.208 14.1113 37.1538 13.6286 37.5357 12.7546L40.9021 5.05064C41.3821 3.95214 42.714 3.51979 43.7477 4.12692L50.942 8.35259C51.7663 8.83672 52.8179 8.66989 53.4519 7.95443L59.0031 1.68939Z"
        fill="#FFC229"
      />
      <path
        d="M23.2767 65.014L20.6307 55.2H22.2267L24.1727 63.32L26.4127 55.2H28.0507L30.2207 63.32L32.1667 55.2H33.7627L31.0747 65H29.3387L27.1967 57.314L24.9847 65L23.2767 65.014ZM36.5243 65V55.2H38.0083V65H36.5243ZM41.3969 65V55.2H42.8809V63.824H47.3329V65H41.3969ZM50.3301 65V55.2H53.5081C54.6561 55.2 55.5988 55.4007 56.3361 55.802C57.0828 56.2033 57.6335 56.7727 57.9881 57.51C58.3521 58.238 58.5341 59.106 58.5341 60.114C58.5341 61.1127 58.3521 61.9807 57.9881 62.718C57.6335 63.446 57.0828 64.0107 56.3361 64.412C55.5988 64.804 54.6561 65 53.5081 65H50.3301ZM51.8141 63.74H53.4521C54.3388 63.74 55.0388 63.5953 55.5521 63.306C56.0748 63.0167 56.4481 62.6013 56.6721 62.06C56.9055 61.5187 57.0221 60.87 57.0221 60.114C57.0221 59.358 56.9055 58.7047 56.6721 58.154C56.4481 57.6033 56.0748 57.1833 55.5521 56.894C55.0388 56.5953 54.3388 56.446 53.4521 56.446H51.8141V63.74ZM65.9419 65.168C64.9712 65.168 64.1312 64.958 63.4219 64.538C62.7219 64.1087 62.1805 63.516 61.7979 62.76C61.4152 61.9947 61.2239 61.1127 61.2239 60.114C61.2239 59.106 61.4152 58.224 61.7979 57.468C62.1805 56.7027 62.7219 56.1053 63.4219 55.676C64.1312 55.2467 64.9712 55.032 65.9419 55.032C67.0899 55.032 68.0279 55.312 68.7559 55.872C69.4839 56.432 69.9459 57.216 70.1419 58.224H68.4899C68.3592 57.6547 68.0792 57.202 67.6499 56.866C67.2205 56.53 66.6465 56.362 65.9279 56.362C64.9479 56.362 64.1732 56.698 63.6039 57.37C63.0345 58.0327 62.7499 58.9473 62.7499 60.114C62.7499 61.2713 63.0345 62.1813 63.6039 62.844C64.1732 63.5067 64.9479 63.838 65.9279 63.838C66.6465 63.838 67.2205 63.6793 67.6499 63.362C68.0792 63.0447 68.3592 62.6107 68.4899 62.06H70.1419C69.9459 63.0307 69.4839 63.7913 68.7559 64.342C68.0279 64.8927 67.0899 65.168 65.9419 65.168ZM72.3654 65L75.9774 55.2H77.6014L81.1854 65H79.6174L78.7774 62.606H74.7734L73.9334 65H72.3654ZM75.1934 61.43H78.3574L76.7754 56.964L75.1934 61.43ZM83.952 65V55.2H87.326C88.1007 55.2 88.7353 55.3307 89.23 55.592C89.734 55.8533 90.1073 56.208 90.35 56.656C90.5927 57.0947 90.714 57.5847 90.714 58.126C90.714 58.7327 90.5507 59.2833 90.224 59.778C89.9067 60.2727 89.4073 60.6273 88.726 60.842L90.812 65H89.09L87.186 61.052H85.436V65H83.952ZM85.436 59.932H87.242C87.914 59.932 88.404 59.7687 88.712 59.442C89.0293 59.1153 89.188 58.6907 89.188 58.168C89.188 57.6453 89.034 57.23 88.726 56.922C88.418 56.6047 87.9187 56.446 87.228 56.446H85.436V59.932ZM93.8696 65V55.2H97.0476C98.1956 55.2 99.1383 55.4007 99.8756 55.802C100.622 56.2033 101.173 56.7727 101.528 57.51C101.892 58.238 102.074 59.106 102.074 60.114C102.074 61.1127 101.892 61.9807 101.528 62.718C101.173 63.446 100.622 64.0107 99.8756 64.412C99.1383 64.804 98.1956 65 97.0476 65H93.8696ZM95.3536 63.74H96.9916C97.8783 63.74 98.5783 63.5953 99.0916 63.306C99.6143 63.0167 99.9876 62.6013 100.212 62.06C100.445 61.5187 100.562 60.87 100.562 60.114C100.562 59.358 100.445 58.7047 100.212 58.154C99.9876 57.6033 99.6143 57.1833 99.0916 56.894C98.5783 56.5953 97.8783 56.446 96.9916 56.446H95.3536V63.74Z"
        fill="white"
      />
      <path
        d="M39.1177 14L40.8856 21.5348L49.0001 23.1765L40.8856 24.8181L39.1177 32.3529L37.3498 24.8181L29.2354 23.1765L37.3498 21.5348L39.1177 14Z"
        fill="white"
      />
      <path
        d="M29.9412 26.7061L30.8251 31.3429L34.8824 32.3531L30.8251 33.3634L29.9412 38.0002L29.0572 33.3634L25 32.3531L29.0572 31.3429L29.9412 26.7061Z"
        fill="white"
      />
      <path
        d="M96.1177 78L97.8856 85.5348L106 87.1765L97.8856 88.8181L96.1177 96.3529L94.3498 88.8181L86.2354 87.1765L94.3498 85.5348L96.1177 78Z"
        fill="white"
      />
      <path
        d="M86.9412 90.7061L87.8251 95.3429L91.8824 96.3531L87.8251 97.3634L86.9412 102L86.0572 97.3634L82 96.3531L86.0572 95.3429L86.9412 90.7061Z"
        fill="white"
      />
    </svg>
  );
}

function StartQuoteIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4.086 8.96C5.454 8.96 6.444 8.294 6.894 7.196C7.38 8.294 8.442 8.96 9.702 8.96C11.538 8.96 12.726 7.7 12.726 5.972C12.726 4.424 11.628 3.164 10.062 3.164C9.288 3.164 8.532 3.416 8.334 3.668C8.334 3.326 8.568 2.894 9.018 2.372C9.9 1.346 11.772 0.823999 12.69 0.823999L12.33 0.319999C9.558 0.319999 7.182 1.958 6.642 4.352C6.138 3.56 5.4 3.164 4.446 3.164C3.672 3.164 2.916 3.416 2.718 3.668C2.718 3.326 2.952 2.894 3.402 2.372C4.284 1.346 6.156 0.823999 7.074 0.823999L6.714 0.319999C5.706 0.319999 4.716 0.553999 3.798 1.004C1.944 1.922 0.918 3.506 0.918 5.36C0.918 7.682 2.286 8.96 4.086 8.96Z"
        fill="#FFC229"
      />
    </svg>
  );
}

function EndQuoteIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 13 9" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M0.673375 8.96C1.44738 8.96 2.23938 8.798 3.06738 8.492C4.72338 7.88 5.98338 6.512 6.36138 4.928C6.86538 5.72 7.60338 6.116 8.55738 6.116C9.29538 6.116 10.0334 5.882 10.2854 5.612C10.2854 5.972 10.0514 6.422 9.60138 6.908C8.68338 7.934 6.84738 8.456 5.92938 8.456L6.28938 8.96C7.17138 8.96 8.07138 8.762 8.98938 8.366C10.8074 7.574 12.0854 5.756 12.0854 3.92C12.0854 1.616 10.7174 0.319999 8.91738 0.319999C7.54938 0.319999 6.55938 0.985999 6.10938 2.084C5.62338 0.985999 4.56138 0.319999 3.30138 0.319999C1.48338 0.319999 0.277375 1.562 0.277375 3.308C0.277375 4.856 1.37538 6.116 2.94138 6.116C3.67938 6.116 4.41738 5.882 4.66938 5.612C4.66938 5.972 4.43538 6.422 3.98537 6.908C3.06738 7.934 1.23138 8.456 0.313375 8.456L0.673375 8.96Z"
        fill="#FFC229"
      />
    </svg>
  );
}
